/* @flow */

import { Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { isObject } from '@e180/util/lib/type';

import PreferencesCheckbox from 'src/shared/app/preference/component/PreferencesCheckbox';
import styles from './UserBaseFieldset.style';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import messages from 'src/shared/domain/user/l10n/userL10n';

type Props = {
  positionLabel: ?string,
  positionPlaceholder: ?string,
  companyLabel: ?string,
  companyPlaceholder: ?string,
  i18nFields: ?Array<Object>,
  extraFields: ?Array<Object>,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const UserBaseFieldsetDumb = ({
  positionLabel,
  positionPlaceholder,
  companyLabel,
  companyPlaceholder,
  i18nFields: propI18nFields,
  extraFields: propExtraFields,
  intl,
}: Props): React.Node => {
  const i18nFields = propI18nFields || [
    {
      name: 'position',
      label: positionLabel || intl.formatMessage(messages.position),
      placeholder: positionPlaceholder,
    },
    {
      name: 'company',
      label: companyLabel || intl.formatMessage(messages.company),
      placeholder: companyPlaceholder,
    },
  ];

  const extraFields = propExtraFields || [];

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const i18nFieldElts = i18nFields
    // Fields can come directly from the API, so we need these verifications
    .filter((field) => isObject(field))
    .filter(({ name }) => !!name)
    .map(({ name, type, label, placeholder, list }) => (
      <Field
        id={name}
        name={name}
        type={type || 'text'}
        component={FormikFormGroup}
        componentOpts={{ label: label || name, list }}
        placeholder={placeholder || null}
        maxLength="100"
        key={name}
      />
    ));

  const extraFieldElts = extraFields
    // Fields can come directly from the API, so we need these verifications
    .filter((field) => isObject(field))
    .filter(({ name }) => !!name)
    .map(({ name, type, label, placeholder, list }) =>
      type === 'checkbox' ? (
        <div className={classes.checkbox} key={name}>
          <PreferencesCheckbox name={name} value={true}>
            {label || name}
          </PreferencesCheckbox>
        </div>
      ) : (
        <Field
          id={name}
          name={name}
          type={type || 'text'}
          component={FormikFormGroup}
          componentOpts={{ label: label || name, list }}
          placeholder={placeholder || null}
          maxLength="100"
          key={name}
        />
      ),
    );

  return (
    <fieldset className={classes.fieldset}>
      <Field
        id="firstName"
        name="firstName"
        type="text"
        component={FormikFormGroup}
        componentOpts={{
          label: intl.formatMessage(messages.firstName),
        }}
        maxLength="100"
      />
      <Field
        id="lastName"
        name="lastName"
        type="text"
        component={FormikFormGroup}
        componentOpts={{
          label: intl.formatMessage(messages.lastName),
        }}
        maxLength="100"
      />
      {i18nFieldElts}
      {extraFieldElts}
    </fieldset>
  );
};

export default compose(React.memo, injectIntl)(UserBaseFieldsetDumb);
